import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {CONFIG_TYPENAME_TO_PARAMETER_TYPE, ConfigTypename} from 'insights/parameters/ParameterConstants';
import {ParameterType} from 'insights/parameters/ParameterTypes';
import {PartyType, PartyTypename} from 'parties/PartyConstants';

export abstract class AsymmetricFieldResolver {
  includeIfNull: boolean;
  excludeCondition?: (translatableObj: unknown) => boolean;

  // This abstraction could be a little better since "include if null" as a concept doesn't apply to GraphQL -
  // the presence of a GraphQL field is purely based on it being in the selection set. Leaving this for now,
  // though, since it's convenient and not costing much.
  constructor(includeIfNull: boolean = true, excludeCondition?: (translatableObj: unknown) => boolean) {
    this.includeIfNull = includeIfNull;
    this.excludeCondition = excludeCondition;
  }

  public abstract getValue(translatableObj: unknown): unknown;
}

class TestOwnerHandleResolver extends AsymmetricFieldResolver {
  // TODO: This expects a GraphQL obj and should be split into a separate class
  public getValue(translatableObj: unknown): unknown {
    // @ts-expect-error
    return translatableObj?.foo?.name ?? null;
  }
}

class TestBiffResolver extends AsymmetricFieldResolver {
  public getValue(): unknown {
    return 'ok';
  }
}

class TestUppercaseNameResolver extends AsymmetricFieldResolver {
  // TODO: This expects a REST obj and should be split into a separate class
  public getValue(translatableObj: unknown): unknown {
    return (translatableObj as {name: string}).name.toUpperCase();
  }
}

class NullResolver extends AsymmetricFieldResolver {
  public getValue(): unknown {
    return null;
  }
}

class ConstantResolver extends AsymmetricFieldResolver {
  value: unknown;

  constructor(
    value: unknown,
    includeIfNull: boolean = true,
    excludeCondition?: (translatableObj: unknown) => boolean
  ) {
    super(includeIfNull, excludeCondition);
    this.value = value;
  }

  public getValue(translatableObj: unknown): unknown {
    return this.value;
  }
}

class TypenameExcludingConstantResolver extends ConstantResolver {
  constructor(value: unknown, typenamesToExclude: Array<string>) {
    super(value, true, (translatableObj: unknown): boolean =>
      typenamesToExclude.includes((translatableObj as {__typename: string}).__typename)
    );
  }
}

class TransposeResolver extends AsymmetricFieldResolver {
  path: Array<string>;
  strictAccess: boolean;
  override includeIfNull: boolean;

  constructor(
    path: Array<string>,
    {strictAccess = true, includeIfNull = true}: {strictAccess?: boolean; includeIfNull?: boolean} = {}
  ) {
    super();
    this.path = path;
    this.strictAccess = strictAccess;
    this.includeIfNull = includeIfNull;
  }

  public getValue(translatableObj: unknown): unknown {
    let sourceValue = translatableObj;
    for (const pathComp of this.path) {
      if (this.strictAccess || sourceValue != null) {
        // @ts-expect-error
        sourceValue = sourceValue[pathComp];
      } else {
        return undefined;
      }
    }
    return sourceValue;
  }
}

class BaseAnalyticsParameterConfigTypeResolver extends AsymmetricFieldResolver {
  public getValue(translatableObj: {__typename: string}): ParameterType {
    return CONFIG_TYPENAME_TO_PARAMETER_TYPE[translatableObj.__typename as ConfigTypename];
  }
}

class OptionalTransposeResolver extends TransposeResolver {
  constructor(path: Array<string>) {
    super(path, {strictAccess: false, includeIfNull: false});
  }
}

class SampleGroupSamplesResolver extends AsymmetricFieldResolver {
  // TODO: This expects a REST obj and should be split into a separate class
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  public getValue(translatableObj: any): unknown {
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    return translatableObj.tags.flatMap((tag: any) =>
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      tag.targetSamples.map((sample: any) => ({__typename: 'ExecutionSampleNode', id: sample.id}))
    );
  }
}

class TemplateOrSubtemplateCreatorResolver extends AsymmetricFieldResolver {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  public getValue(translatableObj: any): unknown {
    return {__typename: 'UserNode', id: translatableObj.creatorId, name: translatableObj.creator};
  }
}

class PartyTypeResolver extends AsymmetricFieldResolver {
  public getValue(translatableObj: {__typename: PartyTypename}): PartyType {
    switch (translatableObj.__typename) {
      case 'UserNode':
        return ModelTypeConstants.USER;
      case 'BenchlingAppNode':
        return ModelTypeConstants.BENCHLING_APP;
      case 'RoutinePrincipalNode':
        return ModelTypeConstants.ROUTINE;
      case 'ServiceAccountActorNode':
        return ModelTypeConstants.SERVICE_ACCOUNT;
      case 'OrganizationNode':
        return ModelTypeConstants.ORGANIZATION;
      case 'TeamNode':
        return ModelTypeConstants.TEAM;
      default:
        assert(false, `Unexpected __typename: ${translatableObj.__typename}`);
    }
  }
}

// Keep in sync with asymmetric_field_resolvers.py
export const ASYMMETRIC_RESOLVER_BY_KEY: {[key: string]: AsymmetricFieldResolver} = {
  'AllSchemaInterfaces:schemaInterfaces': new ConstantResolver([]),
  'BaseAnalyticsParameterConfig:type': new BaseAnalyticsParameterConfigTypeResolver(),
  'Batch:file:result': new ConstantResolver({tags: null}),
  'Container:containerSchemaId': new TransposeResolver(['storableSchema', 'id']),
  'Party:institution': new NullResolver(),
  'Party:proclaimed': new NullResolver(),
  'Party:status': new NullResolver(),
  'Party:type': new PartyTypeResolver(),
  'ExecutionSample:fileId': new TransposeResolver(['bioentity', 'id'], {
    strictAccess: false,
    includeIfNull: true,
  }),
  'Folder:dbId': new TransposeResolver(['dbId']),
  'Folder:owner_handle': new TransposeResolver(['ownerJson', 'handle']),
  'Folder:owner_url_identifier': new TransposeResolver(['ownerJson', 'url_identifier']),
  'Protocol:tagSchema': new NullResolver(),
  'RegistryItem:projectFolderId': new OptionalTransposeResolver(['projectFolder', 'id']),
  'RequestSchema:publishedDataTableColumns': new NullResolver(),
  'ResultBlob:link_type': new ConstantResolver('result_blob'),
  'ResultSchema:prefix': new ConstantResolver(''),
  'ResultSchema:requestTaskSchemaIds': new ConstantResolver([]),
  'ResultSchema:requestTemplateIds': new ConstantResolver([]),
  'ResultSchema:sampleGroupSchema': new NullResolver(),
  'Run:schemaName': new TransposeResolver(['schema', 'name']),
  'RunSchema:prefix': new ConstantResolver(''),
  'RunSchema:publishedDataTableColumns': new NullResolver(),
  'RunSchema:requestTaskSchemaIds': new ConstantResolver([]),
  'RunSchema:requestTemplateIds': new ConstantResolver([]),
  'RunSchema:sampleGroupSchema': new NullResolver(),
  'SampleGroup:samples': new SampleGroupSamplesResolver(),
  'Sequence:owner': new TransposeResolver(['folder', 'ownerJson', 'handle']),
  'Sequence:creator_id': new TransposeResolver(['creator', 'id']),
  'Sequence:parent_id': new OptionalTransposeResolver(['parentJson', 'parentId']),
  'Sequence:parent_url': new OptionalTransposeResolver(['parentJson', 'parentUrl']),
  'Sequence:parent_version_id': new OptionalTransposeResolver(['parentJson', 'parentVersionId']),
  'Sequence:parent_version_name': new OptionalTransposeResolver(['parentJson', 'parentVersionName']),
  'Sequence:type': new TransposeResolver(['sequenceType']),
  'Storable:sourceId': new TransposeResolver(['source', 'id']),
  'SubtemplateSummary:creator': new TemplateOrSubtemplateCreatorResolver(),
  'SubtemplateSummary:creatorId': new TransposeResolver(['creator', 'id']),
  'SubtemplateSummary:creatorName': new TransposeResolver(['creator', 'name']),
  'TagSchema:resultSchema': new NullResolver(),
  'TemplateSummary:creator': new TemplateOrSubtemplateCreatorResolver(),
  'TemplateSummary:creatorId': new TransposeResolver(['creator', 'id']),
  'TemplateSummary:creatorName': new TransposeResolver(['creator', 'name']),
  'Test:bar': new NullResolver(),
  'Test:baz': new ConstantResolver({id: 'baz_789'}),
  'Test:biff': new TestBiffResolver(),
  'Test:exc': new TypenameExcludingConstantResolver('blah', ['Test']), // Remove when we have a real use case
  'Test:optionalFieldId': new OptionalTransposeResolver(['optionalFieldForRestWithSpread', 'id']),
  'Test:owner_handle': new TestOwnerHandleResolver(),
  'Test:uppercaseName': new TestUppercaseNameResolver(),
};
